"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { auth, getFirebaseAuth } from "../api/firebase-ultra-safe";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { getUserProfile } from "../profile/api";
import { User } from "../../data/types";
import { UserProfile } from "../../data/profile/types";
import { useSession } from "next-auth/react";
import { getFirebaseDb } from "../api/firebase-ultra-safe";
import { doc, getDoc } from "firebase/firestore";

// UserProfile을 User로 변환하는 함수
function convertUserProfileToUser(
  profile: UserProfile,
  firebaseUser?: FirebaseUser
): User {
  return {
    id: profile.uid,
    uid: profile.uid,
    email: profile.email || firebaseUser?.email || "",
    phoneNumber: profile.phoneNumber,
    nickname: profile.nickname || firebaseUser?.displayName || "사용자",
    region: profile.region || "지역 정보 없음",
    grade: profile.grade || "C",
    tradeCount: profile.tradesCount || 0,
    reviewCount: profile.reviewsCount || 0,
    createdAt: profile.createdAt?.toDate() || new Date(),
    updatedAt: profile.updatedAt?.toDate() || new Date(),
    profileImage: profile.photoURL || firebaseUser?.photoURL || undefined,
    safeTransactionCount: 0,
    averageRating: 0,
    disputeCount: 0,
    isPhoneVerified: profile.phoneVerified || false,
  };
}

// NextAuth 세션에서 Firebase DB 프로필을 가져오는 함수
async function getNextAuthUserProfile(session: any): Promise<User | null> {
  try {
    if (!session?.user) return null;

    const userId =
      session.user.id ||
      `google_${session.user.email?.replace("@", "_").replace(".", "_")}`;

    // 서버 사이드 API를 통해 프로필 조회 (권한 문제 해결)
    try {
      const response = await fetch(`/api/profile/get?uid=${encodeURIComponent(userId)}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const userData = result.data;
          console.log("✅ NextAuth 사용자 프로필 로드 성공 (서버 API):", userData.nickname);

          const userProfile: UserProfile = {
            uid: userData.uid,
            email: userData.email,
            nickname: userData.nickname,
            photoURL: userData.photoURL || userData.profileImage,
            phoneNumber: userData.phoneNumber,
            region: userData.region,
            grade: userData.grade,
            tradesCount: userData.tradeCount || userData.tradesCount || 0,
            reviewsCount: userData.reviewCount || userData.reviewsCount || 0,
            phoneVerified: userData.phoneVerified || userData.isPhoneVerified || false,
            createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt),
            updatedAt: userData.updatedAt?.toDate ? userData.updatedAt.toDate() : new Date(userData.updatedAt),
          };

          return convertUserProfileToUser(userProfile);
        }
      }
    } catch (apiError) {
      // API 실패 시 조용히 처리 (에러 로그만)
      console.warn("⚠️ 서버 사이드 프로필 조회 실패 (조용히 처리):", apiError);
    }

    return null;
  } catch (error) {
    // 에러 발생 시 조용히 처리
    console.warn("⚠️ NextAuth 사용자 프로필 로드 실패 (조용히 처리):", error);
    return null;
  }
}

// 전역 상태 관리
let globalUser: User | null = null;
let globalLoading = true;
const globalListeners: Set<() => void> = new Set();

function notifyListeners() {
  globalListeners.forEach(listener => listener());
}

// Firebase 사용자 처리 함수
async function processFirebaseUser(
  firebaseUser: FirebaseUser | null
): Promise<User | null> {
  if (!firebaseUser) {
    return null;
  }

  try {
    const userData = await getUserProfile(firebaseUser.uid);
    if (userData && userData.success && userData.data) {
      return convertUserProfileToUser(userData.data, firebaseUser);
    } else {
      return {
        id: firebaseUser.uid,
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
        phoneNumber: undefined,
        nickname:
          firebaseUser.displayName ||
          firebaseUser.email?.split("@")[0] ||
          "사용자",
        region: "지역 정보 없음",
        grade: "C",
        tradeCount: 0,
        reviewCount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        profileImage: firebaseUser.photoURL || undefined,
        safeTransactionCount: 0,
        averageRating: 0,
        disputeCount: 0,
        isPhoneVerified: false,
      };
    }
  } catch (error) {
    console.error("사용자 정보 가져오기 실패:", error);
    return {
      id: firebaseUser.uid,
      uid: firebaseUser.uid,
      email: firebaseUser.email || "",
      phoneNumber: undefined,
      nickname: firebaseUser.displayName || "사용자",
      region: "",
      grade: "C",
      tradeCount: 0,
      reviewCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      profileImage: firebaseUser.photoURL || undefined,
      safeTransactionCount: 0,
      averageRating: 0,
      disputeCount: 0,
      isPhoneVerified: false,
    };
  }
}

// Firebase Auth 초기화
let isInitialized = false;
let unsubscribe: (() => void) | null = null;

function initializeAuth() {
  if (isInitialized || !auth) {
    return;
  }

  isInitialized = true;
  console.log("🔥 useAuth: Firebase Auth 초기화");

  const currentUser = auth.currentUser;
  if (currentUser) {
    console.log("🔥 useAuth: 현재 Firebase 사용자 발견:", currentUser.uid);
    processFirebaseUser(currentUser).then(user => {
      globalUser = user;
      globalLoading = false;
      notifyListeners();
    });
  } else {
    globalUser = null;
    globalLoading = false;
    notifyListeners();
  }

  unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
    console.log(
      "🔥 useAuth: Firebase Auth 상태 변경:",
      firebaseUser?.uid || "null"
    );

    if (firebaseUser) {
      const user = await processFirebaseUser(firebaseUser);
      globalUser = user;
    } else {
      globalUser = null;
    }

    globalLoading = false;
    notifyListeners();
  });
}

function cleanup() {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  isInitialized = false;
  globalUser = null;
  globalLoading = true;
  globalListeners.clear();
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(globalUser);
  const [loading, setLoading] = useState(globalLoading);
  const listenerRef = useRef<() => void>();
  const isFirstRunRef = useRef(true); // 첫 실행 여부 추적
  const { data: session, status: sessionStatus } = useSession();

  // 개발용 더미 사용자 확인
  useEffect(() => {
    if (typeof window !== "undefined" && !globalUser) {
      const devDummyUser = localStorage.getItem("dev-dummy-user");
      if (devDummyUser) {
        try {
          const dummyUser = JSON.parse(devDummyUser);
          console.log("🧪 개발용 더미 사용자 로드됨:", dummyUser);
          setUser(dummyUser);
          setLoading(false);
          globalUser = dummyUser;
          globalLoading = false;
          globalListeners.forEach(listener => listener());
        } catch (error) {
          console.error("❌ 더미 사용자 파싱 실패:", error);
          localStorage.removeItem("dev-dummy-user");
        }
      }
    }
  }, []);

  // 리스너 등록/해제
  useEffect(() => {
    initializeAuth();

    listenerRef.current = () => {
      setUser(globalUser);
      setLoading(globalLoading);
    };

    globalListeners.add(listenerRef.current);
    setUser(globalUser);
    setLoading(globalLoading);

    return () => {
      if (listenerRef.current) {
        globalListeners.delete(listenerRef.current);
      }
    };
  }, []);

  // NextAuth 세션 처리
  useEffect(() => {
    // 세션이 로딩 중이면 대기
    if (sessionStatus === "loading") {
      return;
    }

    // 사용자가 이미 로드되어 있으면 건너뜀
    if (globalUser && session?.user?.email && globalUser.email === session.user.email) {
      // 로딩 상태만 업데이트
      if (globalLoading) {
        globalLoading = false;
        notifyListeners();
      }
      return;
    }

    const handleNextAuthSession = async () => {
      if (session?.user) {
        
        // 프로필이 생성될 때까지 최대 3초 대기하며 재시도
        let retryCount = 0;
        const maxRetries = 6; // 0.5초 간격으로 6번 = 3초
        
        const tryLoadProfile = async (): Promise<void> => {
          try {
            // 서버 사이드 API로 먼저 시도 (더 빠름)
            const userId = session.user.id || `google_${session.user.email?.replace("@", "_").replace(".", "_")}`;
            
            if (retryCount === 0) {
              // 첫 시도: 서버 사이드 API 사용
              try {
                const response = await fetch(`/api/profile/get?uid=${encodeURIComponent(userId)}`);
                if (response.ok) {
                  const result = await response.json();
                  if (result.success && result.data) {
                    // UserProfile을 User로 변환
                    const userProfile = result.data;
                    const nextAuthUser: User = {
                      id: userProfile.uid,
                      uid: userProfile.uid,
                      email: userProfile.email || "",
                      phoneNumber: userProfile.phoneNumber,
                      nickname: userProfile.nickname || "사용자",
                      region: userProfile.region || "지역 정보 없음",
                      grade: userProfile.grade || "C",
                      tradeCount: userProfile.tradesCount || 0,
                      reviewCount: userProfile.reviewsCount || 0,
                      createdAt: userProfile.createdAt?.toDate?.() || new Date(),
                      updatedAt: userProfile.updatedAt?.toDate?.() || new Date(),
                      profileImage: userProfile.photoURL || userProfile.profileImage,
                      safeTransactionCount: 0,
                      averageRating: 0,
                      disputeCount: 0,
                      isPhoneVerified: userProfile.phoneVerified || false,
                    };
                    globalUser = nextAuthUser;
                    globalLoading = false;
                    notifyListeners();
                    return;
                  }
                }
              } catch (apiError) {
                console.log("⚠️ 서버 사이드 API 실패, 클라이언트 사이드로 폴백");
              }
            }
            
            // 클라이언트 사이드에서 프로필 로드 시도
            const nextAuthUser = await getNextAuthUserProfile(session);
            if (nextAuthUser) {
              globalUser = nextAuthUser;
              globalLoading = false;
              notifyListeners();
              return; // 성공 시 종료
            } else if (retryCount < maxRetries) {
              // 프로필이 아직 생성되지 않았으면 재시도
              retryCount++;
              setTimeout(tryLoadProfile, 500);
            } else {
              // 재시도 실패 시 세션 정보로 임시 사용자 생성
              const tempUser: User = {
                id: userId,
                uid: userId,
                email: session.user.email || "",
                phoneNumber: undefined,
                nickname: session.user.name || session.user.email?.split("@")[0] || "사용자",
                region: "지역 정보 없음",
                grade: "C",
                tradeCount: 0,
                reviewCount: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
                profileImage: session.user.image || undefined,
                safeTransactionCount: 0,
                averageRating: 0,
                disputeCount: 0,
                isPhoneVerified: false,
              };
              globalUser = tempUser;
              globalLoading = false;
              notifyListeners();
            }
          } catch (error) {
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(tryLoadProfile, 500);
            }
          }
        };
        
        tryLoadProfile();
      } else {
        // 세션이 없을 때 - Firebase Auth 사용자가 있으면 유지
        const auth = getFirebaseAuth();
        const firebaseUser = auth?.currentUser;
        if (!firebaseUser && globalUser) {
          globalUser = null;
          globalLoading = false;
          notifyListeners();
        }
      }
    };

    handleNextAuthSession();
  }, [session?.user?.email, sessionStatus]); // email만 의존성에 추가 (전체 session 객체 말고)

  // 사용자 정보 새로고침 함수
  const refreshUser = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const userData = await getUserProfile(user.uid);
      if (userData && userData.success && userData.data && auth.currentUser) {
        const updatedUser = convertUserProfileToUser(
          userData.data,
          auth.currentUser
        );
        globalUser = updatedUser;
        notifyListeners();
      }
    } catch (error) {
      console.error("사용자 정보 새로고침 실패:", error);
    }
  }, [user?.uid]);

  // 사용자 정보 업데이트 함수
  const updateUser = useCallback((updatedUserData: Partial<User>) => {
    if (globalUser) {
      globalUser = { ...globalUser, ...updatedUserData };
      notifyListeners();
    }
  }, []);

  // 로그아웃 함수
  const logout = useCallback(async () => {
    try {
      await auth.signOut();
      globalUser = null;
      globalLoading = false;
      notifyListeners();
    } catch (error) {
      console.error("로그아웃 실패:", error);
    }
  }, []);

  return {
    user,
    loading,
    isLoading: loading, // 하위 호환
    isAuthenticated: !!user,
    refreshUser,
    logout,
    updateUser,
  };
}

// 개발 환경에서만 전역 정리 함수 노출
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as unknown as Record<string, unknown>).__cleanupAuth = cleanup;
}
